import React from 'react'
import { useI18n } from '../../i18n'
import LifecycleBadge from './LifecycleBadge'
import styles from '../../styles/components/assets/AssetsDataMap.module.scss'
import type { DataMapRow } from './dataMapRowParts'

interface AssignFormState {
  category: string
  classification: string
  processingPurpose: string
  storageLocation: string
  retentionPeriod: string
  crossBorderTransfer: boolean
  notes: string
}

interface Props {
  row: DataMapRow
  catalog: string[]
  submitting: boolean
  onClose: () => void
  onRemove: (dataCategoryId: string) => void
  onAssign: (payload: Omit<AssignFormState, never>) => void
}

export default function DataMapDetailPanel({
  row,
  catalog,
  submitting,
  onClose,
  onRemove,
  onAssign,
}: Props) {
  const { t } = useI18n()
  const [form, setForm] = React.useState<AssignFormState>({
    category: '',
    classification: '',
    processingPurpose: '',
    storageLocation: '',
    retentionPeriod: '',
    crossBorderTransfer: false,
    notes: '',
  })

  const withClassification = row.dataCategories.filter((c) => c.classification).length

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.category) return
    onAssign(form)
    setForm({
      category: '',
      classification: '',
      processingPurpose: '',
      storageLocation: '',
      retentionPeriod: '',
      crossBorderTransfer: false,
      notes: '',
    })
  }

  return (
    <div className={styles.detailsPanel}>
      <header className={styles.detailsPanelHeader}>
        <div>
          <p className={styles.detailsPanelLabel}>{t('assets.dataMap.selectedSystem', 'Selected system')}</p>
          <div className={styles.identityHeader}>
            <h3 className={styles.detailsPanelTitle}>{row.informationSystemName}</h3>
            <LifecycleBadge status={row.lifecycleStatus} />
          </div>
          {row.informationSystemCode ? (
            <p className={styles.systemMeta}>{row.informationSystemCode}</p>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label={t('assets.postureRow.closeDetails', 'Close')}
        >
          {t('assets.postureRow.closeDetails', 'Close')}
        </button>
      </header>
      <div className={styles.detailsPanelBody}>
        <div className={styles.detailsKpiGrid}>
          <div className={styles.detailsKpi}>
            <p className={styles.detailsKpiLabel}>{t('assets.dataMap.categoryCount', 'Categories')}</p>
            <p className={styles.detailsKpiValue}>{row.dataCategories.length}</p>
          </div>
          <div className={styles.detailsKpi}>
            <p className={styles.detailsKpiLabel}>{t('assets.dataMap.classification', 'Classification')}</p>
            <p className={styles.detailsKpiValue}>{withClassification}</p>
          </div>
        </div>

        <p className={styles.sectionHeader}>{t('assets.dataMap.mapTitle', 'Assigned categories')}</p>
        {row.dataCategories.length === 0 ? (
          <p className={styles.widgetEmpty}>{t('assets.dataMap.noCategoriesForSystem', 'No categories.')}</p>
        ) : (
          <ul className={styles.categoryList}>
            {row.dataCategories.map((dc) => (
              <li key={dc.id} className={styles.categoryListItem}>
                <div className={styles.categoryListMain}>
                  <p className={styles.categoryListName}>{dc.category}</p>
                  {dc.classification ? (
                    <p className={styles.categoryListMeta}>{dc.classification}</p>
                  ) : null}
                </div>
                <button type="button" className={styles.removeBtn} onClick={() => onRemove(dc.id)}>
                  {t('common.remove', 'Remove')}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.assignBlock}>
          <h4 className={styles.assignBlockTitle}>{t('assets.dataMap.assignTitle', 'Assign data category')}</h4>
          <form className={styles.assignForm} onSubmit={handleSubmit}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="dm-category">
                {t('assets.dataMap.category', 'Data category')}
              </label>
              <select
                id="dm-category"
                className={styles.selectField}
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                required
                style={{ maxWidth: '100%', width: '100%' }}
              >
                <option value="">{t('assets.dataMap.selectCategory', 'Select category…')}</option>
                {catalog.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="dm-classification">
                {t('assets.dataMap.classification', 'Classification')}
              </label>
              <input
                id="dm-classification"
                className={styles.textField}
                value={form.classification}
                onChange={(e) => setForm((f) => ({ ...f, classification: e.target.value }))}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="dm-purpose">
                {t('assets.dataMap.processingPurpose', 'Processing purpose')}
              </label>
              <input
                id="dm-purpose"
                className={styles.textField}
                value={form.processingPurpose}
                onChange={(e) => setForm((f) => ({ ...f, processingPurpose: e.target.value }))}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="dm-storage">
                {t('assets.dataMap.storageLocation', 'Storage location')}
              </label>
              <input
                id="dm-storage"
                className={styles.textField}
                value={form.storageLocation}
                onChange={(e) => setForm((f) => ({ ...f, storageLocation: e.target.value }))}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="dm-retention">
                {t('assets.dataMap.retentionPeriod', 'Retention period')}
              </label>
              <input
                id="dm-retention"
                className={styles.textField}
                value={form.retentionPeriod}
                onChange={(e) => setForm((f) => ({ ...f, retentionPeriod: e.target.value }))}
              />
            </div>
            <div className={styles.checkboxRow}>
              <input
                type="checkbox"
                id="dm-cross-border"
                checked={form.crossBorderTransfer}
                onChange={(e) => setForm((f) => ({ ...f, crossBorderTransfer: e.target.checked }))}
              />
              <label htmlFor="dm-cross-border">{t('assets.dataMap.crossBorder', 'Cross-border transfer')}</label>
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="dm-notes">
                {t('assets.dataMap.notes', 'Notes')}
              </label>
              <input
                id="dm-notes"
                className={styles.textField}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            <button type="submit" className={styles.primaryBtn} disabled={submitting || !form.category}>
              {t('assets.dataMap.assign', 'Assign')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export function DataMapDetailEmpty() {
  const { t } = useI18n()
  return (
    <div className={styles.detailsPanelEmpty}>
      {t('assets.dataMap.selectSystemHint', 'Select a system to view categories and assign data.')}
    </div>
  )
}
